import './ShipmentShow.scss';

import { DateField, Show, TextField } from '@refinedev/antd';
import { Divider, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { useCountryEnforcer } from '@/components/general/CountryEnforcer';
import LinkTextField from '@/components/general/LinkTextField';
import { ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { COMPANY_URLS } from '@/constants/urls';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';

import ShipmentShowAddress from './ShipmentShow/ShipmentShowAddress';
import ShipmentShowPayment from './ShipmentShow/ShipmentShowAdmin/ShipmentShowPayment';
import ShipmentShowDetail from './ShipmentShow/ShipmentShowDetail';
import ShipmentShowFiles from './ShipmentShow/ShipmentShowFiles';
import ShipmentShowHeaderButtons from './ShipmentShow/ShipmentShowHeader/ShipmentShowHeaderButtons';
import ShipmentShowLogistic from './ShipmentShow/ShipmentShowLogistic/ShipmentShowLogistic';
import useShipmentShow from './ShipmentShow/useShipmentShow';

export const ShipmentShow = () => {
  const { alert, blockEditForCountry } = useCountryEnforcer();
  const { t } = useTranslation();
  const {
    id,
    result,
    loading,
    collectionShow,
    refetchResource,
    blockChangeState,
    setBlockChangeState,
    itemsEditMode,
    canSend,
    isPaid,
    showQuantityAlert,
    createdBy,
    ticket,
    technician,
    packageItems,
    setPackageItems,
    hasTaxFileUploaded,
  } = useShipmentShow();

  return (
    <Show
      title={
        collectionShow
          ? t('Venta de repuestos envío') + ' #' + id
          : t('Detalles del envío') + ' #' + id
      }
      isLoading={loading}
      headerButtons={buttonProps =>
        result && (
          <ShipmentShowHeaderButtons
            record={result}
            refineButtonsProps={buttonProps}
            refetch={() => void refetchResource()}
            blockChangeState={blockChangeState}
            canSend={canSend}
            isPaid={isPaid}
            showQuantityAlert={showQuantityAlert}
            hasTaxFileUploaded={hasTaxFileUploaded}
            blockForCountry={blockEditForCountry}
          />
        )
      }>
      {alert}
      <div className="ShipmentShow">
        <div className="ShipmentShowDataRow">
          <div className="ShipmentShowData ShipmentShowDataId">
            <b>
              {t('Id de envío')}: {result?.id}
            </b>
            {result?.status !== undefined && (
              <div className="TicketShowRowBooleans">
                <PackageStateTag packageStatus={result.status} />
                {result?.tipo === SHIPMENT_TYPE.AUTOMATICO && (
                  <Tag color="geekblue">{t('Automático')}</Tag>
                )}
              </div>
            )}
          </div>

          <div className="ShipmentShowData">
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={result?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={result?.updated_at} />
            </div>

            <div>
              <b>{t('Creado por')}: </b>
              <TextField
                value={
                  createdBy
                    ? `${createdBy?.first_name} ${createdBy?.last_name} (${createdBy?.id})`
                    : ''
                }
              />
            </div>
          </div>
        </div>

        {result?.status === PACKAGE_STATES.CANCELADO && result?.cancellation_motive && (
          <ShowSection title={t('Motivo de cancelación')}>
            <TextField value={result?.cancellation_motive} />
          </ShowSection>
        )}

        <div className="ShipmentShowContentLayout">
          <ShowSection title={t('Detalles de facturación')}>
            <ShipmentShowPayment
              record={result}
              packageItems={packageItems}
              refetch={refetchResource}
            />
          </ShowSection>

          <ShowSection title={t('Detalles del pedido')}>
            <ShipmentShowDetail record={result} ticket={ticket} />
          </ShowSection>

          <ShowSection title={t('Dirección de envío')}>
            <ShipmentShowAddress record={result} />
          </ShowSection>

          {(result?.status ?? 0) > PACKAGE_STATES.PENDIENTE && (
            <ShowSection title={t('Logística')}>
              <ShipmentShowLogistic
                record={result}
                refetch={() => void refetchResource()}
                setBlockChangeState={setBlockChangeState}
              />
            </ShowSection>
          )}

          <ShowSection title={t('Técnico involucrado')}>
            <ShowInfoItem>
              {result?.technician && technician ? (
                <LinkTextField
                  label={`${technician?.full_name} (${technician?.id})`}
                  to={`${COMPANY_URLS.ADMIN_EMPRESA}/${technician?.id}`}
                  admin
                />
              ) : (
                <span>{t('No habrá técnico involucrado')}</span>
              )}
            </ShowInfoItem>
          </ShowSection>
        </div>

        <ShowSection title={t('Elementos')} withoutCard>
          <PackageItemsList
            packageItems={packageItems}
            setPackageItems={setPackageItems}
            editMode={itemsEditMode}
            package={{ id: result?.id }}
            setBlockChangeState={setBlockChangeState}
            refetchResource={() => void refetchResource()}
          />
        </ShowSection>

        {result?.status !== undefined &&
          result.status >= PACKAGE_STATES.PREPARADO &&
          result.status <= PACKAGE_STATES.FINALIZADO && (
            <>
              <Divider />
              <ShowSection title={t('Fotos')} withoutCard>
                <ShipmentShowFiles record={result} canEdit={!blockEditForCountry} />
              </ShowSection>
            </>
          )}
      </div>
    </Show>
  );
};
